import { CHANNELS } from "../../shared/channels";
import { ProfileBoard, ProfileCard } from "../../shared/types";
import { call, isWeb, mediaSrc } from "../bridge";
import { byId } from "../dom";
import { stampIcons } from "../icons";
import { t } from "../i18n";
import { scrollBehavior } from "../motion_pref";
import { MyProfile, canUse, myId, myProfiles } from "../me";
import { onViewShown, showView } from "../navigation";
import { flash } from "../tweens";

// your profile: picture, name, pronouns and colour. a system also sees its alters here and can make more,
// give each a picture and add the discord accounts that own them. the switcher swaps who the whole app acts as

let board: ProfileBoard | null = null;
// whose card is open for editing, the person themself or one of their alters
let editing = "";

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function errorOf(error: unknown): string {
  return String(error instanceof Error ? error.message : error).replace(/^.*Error: /, "");
}

function setStatus(text: string): void {
  const status = byId("profile-status");
  status.textContent = text;
  flash(status);
}

// a square picture, or the first letter on their colour
export function avatarEl(who: { name: string; color: string; avatar: string }, size: "" | "big" | "small" = ""): HTMLElement {
  const node = el("span", `pfp ${size}`.trim());
  if (who.color) node.style.setProperty("--pfp", who.color);
  if (who.avatar) node.style.backgroundImage = `url("${mediaSrc(who.avatar)}")`;
  else node.textContent = (Array.from(who.name.trim())[0] || "?").toUpperCase();
  node.setAttribute("role", "img");
  node.setAttribute("aria-label", who.name);
  return node;
}

async function switchTo(id: string): Promise<void> {
  if (id === myId()) return;
  const response = await fetch("/auth/switch", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) });
  if (response.ok) location.reload();
  else setStatus(t("couldn't switch"));
}

// who's here, the sheet over everything
function openWho(open: boolean): void {
  byId("who-sheet").setAttribute("aria-hidden", String(!open));
  document.body.classList.toggle("who-open", open);
}

function paintWho(): void {
  const profiles = myProfiles();
  const chip = byId("who-chip");
  const mine = profiles.find((profile) => profile.id === myId());
  chip.hidden = !mine;
  if (mine) chip.replaceChildren(avatarEl(mine, "small"), el("span", "", mine.name), el("em", "", profiles.length > 1 ? t("switch") : t("profile")));
  byId("who-list").replaceChildren(...profiles.map((profile) => whoRow(profile)));
  byId("profile-switch").hidden = !isWeb() || profiles.length < 2;
}

function whoRow(profile: MyProfile): HTMLElement {
  const row = el("button", "who-row");
  row.type = "button";
  row.classList.toggle("on", profile.id === myId());
  row.append(avatarEl(profile), el("b", "", profile.name), el("small", "", profile.alter ? t("alter") : t("system")));
  row.addEventListener("click", () => {
    openWho(false);
    void switchTo(profile.id);
  });
  return row;
}

async function load(): Promise<void> {
  try {
    board = await call<ProfileBoard>(CHANNELS.profileGet);
    if (!editing || ![board.me, ...board.alters].some((card) => card.id === editing)) editing = board.me.id;
    render();
  } catch (error) {
    setStatus(errorOf(error));
  }
}

async function run(work: () => Promise<ProfileBoard>, done = ""): Promise<void> {
  try {
    board = await work();
    if (done) setStatus(done);
    render();
  } catch (error) {
    setStatus(errorOf(error));
  }
}

// any picture, cropped to the middle square and squeezed to 256 so it stays small on the server
function squared(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const side = Math.min(image.naturalWidth, image.naturalHeight);
      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 256;
      const ink = canvas.getContext("2d");
      if (!ink || !side) return reject(new Error(t("that isn't a picture")));
      ink.drawImage(image, (image.naturalWidth - side) / 2, (image.naturalHeight - side) / 2, side, side, 0, 0, 256, 256);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.88));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(t("that isn't a picture")));
    };
    image.src = url;
  });
}

function field(label: string, input: HTMLElement): HTMLElement {
  const wrap = el("label", "field", label);
  wrap.append(input);
  return wrap;
}

function pictureKeys(card: ProfileCard): HTMLElement {
  const row = el("div", "row wrap profile-keys");
  const file = el("input");
  file.type = "file";
  file.accept = "image/png,image/jpeg,image/webp,image/gif";
  file.hidden = true;
  file.addEventListener("change", async () => {
    const picked = file.files?.[0];
    if (!picked) return;
    try {
      const data = await squared(picked);
      await run(() => call<ProfileBoard>(CHANNELS.profileAvatar, card.id, "upload", data), t("picture saved"));
    } catch (error) {
      setStatus(errorOf(error));
    }
  });
  const upload = el("button", "pill small");
  upload.type = "button";
  upload.innerHTML = `<i data-icon="plus" data-size="14"></i><span></span>`;
  upload.querySelector("span")!.textContent = t("upload picture");
  upload.addEventListener("click", () => file.click());
  row.append(upload, file);
  for (const source of card.discord) {
    const key = el("button", "pill small discord-pick");
    key.type = "button";
    const thumb = el("img");
    thumb.src = source.url;
    thumb.alt = "";
    key.append(thumb, el("span", "", t("use {name}'s discord picture", { name: source.name })));
    key.addEventListener("click", () => void run(() => call<ProfileBoard>(CHANNELS.profileAvatar, card.id, "discord", source.id), t("picture saved")));
    row.append(key);
  }
  if (card.avatar) {
    const clear = el("button", "pill small", t("remove picture"));
    clear.type = "button";
    clear.addEventListener("click", () => void run(() => call<ProfileBoard>(CHANNELS.profileAvatar, card.id, "clear", ""), t("picture removed")));
    row.append(clear);
  }
  return row;
}

function ownersBlock(card: ProfileCard): HTMLElement {
  const wrap = el("div", "profile-owners");
  wrap.append(el("span", "field-label", t("owned by")));
  const list = el("ul", "owner-list");
  for (const owner of card.owners) {
    const item = el("li");
    item.append(el("span", "", owner.name), el("small", "", owner.system ? t("system") : t("discord {id}", { id: owner.id })));
    const drop = el("button", "pill small", t("remove"));
    drop.type = "button";
    drop.setAttribute("aria-label", t("remove {name} as an owner", { name: owner.name }));
    drop.addEventListener("click", () => void run(() => call<ProfileBoard>(CHANNELS.altersOwner, card.id, owner.id, false), t("owner removed")));
    if (card.owners.length > 1) item.append(drop);
    list.append(item);
  }
  const form = el("form", "inline-form");
  const input = el("input");
  input.placeholder = t("another discord user id");
  input.inputMode = "numeric";
  input.autocomplete = "off";
  input.setAttribute("aria-label", t("discord user id"));
  const add = el("button", "pill small accent", t("add owner"));
  add.type = "submit";
  form.append(input, add);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void run(() => call<ProfileBoard>(CHANNELS.altersOwner, card.id, input.value.trim(), true), t("they can sign in as {name} now", { name: card.name }));
  });
  wrap.append(list, form, el("span", "hint", t("a discord account that owns an alter can sign in and switch to it, so give the alter's own account here")));
  return wrap;
}

function editor(card: ProfileCard, canRename: boolean): HTMLElement {
  const wrap = el("div", "stack profile-edit");
  const head = el("div", "profile-head");
  const names = el("div", "profile-names");
  const title = el("b", "profile-name", card.name);
  if (card.color) title.style.color = card.color;
  names.append(title, el("small", "", card.pronouns || (card.alter ? t("alter") : t("system"))));
  head.append(avatarEl(card, "big"), names);

  const name = el("input");
  name.value = card.name;
  name.maxLength = 40;
  name.disabled = !canRename;
  const pronouns = el("input");
  pronouns.value = card.pronouns;
  pronouns.maxLength = 40;
  pronouns.placeholder = t("she/her, they/them…");
  const color = el("input");
  color.type = "color";
  color.value = card.color || "#4de8ff";
  const bio = el("textarea");
  bio.value = card.bio;
  bio.rows = 3;
  bio.maxLength = 400;
  bio.placeholder = t("a line about them");
  const save = el("button", "pill accent", t("save"));
  save.type = "button";
  save.addEventListener("click", () => void run(() => call<ProfileBoard>(CHANNELS.profileSave, card.id, {
    name: canRename ? name.value : undefined,
    pronouns: pronouns.value,
    color: color.value,
    bio: bio.value
  }), t("saved")));
  wrap.append(head, pictureKeys(card), field(t("name"), name), field(t("pronouns"), pronouns), field(t("colour"), color), field(t("about"), bio));
  if (card.alter) wrap.append(ownersBlock(card));
  wrap.append(save);
  return wrap;
}

function alterTile(card: ProfileCard): HTMLElement {
  const tile = el("div", "alter-tile");
  tile.classList.toggle("on", card.id === editing);
  const open = el("button", "alter-open");
  open.type = "button";
  open.append(avatarEl(card), el("b", "", card.name), el("small", "", card.pronouns));
  open.addEventListener("click", () => {
    editing = card.id;
    render();
    byId("profile-card").scrollIntoView({ block: "nearest", behavior: scrollBehavior() });
  });
  tile.append(open);
  if (isWeb()) {
    const act = el("button", "pill small", t("be {name}", { name: card.name }));
    act.type = "button";
    act.addEventListener("click", () => void switchTo(card.id));
    tile.append(act);
  }
  return tile;
}

function render(): void {
  if (!board) return;
  const everyone = [board.me, ...board.alters];
  const card = everyone.find((item) => item.id === editing) || board.me;
  const shown = el("div", "stack");
  if (board.me.alter) {
    const back = el("div", "profile-from");
    back.append(avatarEl(board.system, "small"), el("span", "", t("{name} is part of {system}", { name: board.me.name, system: board.system.name })));
    shown.append(back);
  } else if (card.id !== board.me.id) {
    const mine = el("button", "pill small", t("back to my profile"));
    mine.type = "button";
    mine.addEventListener("click", () => {
      editing = board!.me.id;
      render();
    });
    shown.append(mine);
  }
  shown.append(editor(card, card.alter || !board.me.alter));
  if (card.alter && !board.me.alter) {
    const remove = el("button", "pill small danger", t("remove {name}", { name: card.name }));
    remove.type = "button";
    remove.addEventListener("click", () => {
      if (!confirm(t("remove {name}? their notes and chats stay on the server if you add them back", { name: card.name }))) return;
      editing = board!.me.id;
      void run(() => call<ProfileBoard>(CHANNELS.altersRemove, card.id), t("{name} removed", { name: card.name }));
    });
    shown.append(remove);
  }
  byId("profile-card").replaceChildren(shown);
  const holder = byId("profile-alters");
  holder.hidden = !board.canMake;
  byId("profile-alter-count").textContent = String(board.alters.length);
  byId("profile-alter-list").replaceChildren(...(board.alters.length ? board.alters.map(alterTile) : [el("p", "empty", t("no alters yet. add the first one above"))]));
  stampIcons(byId("view-profile"));
  paintWho();
}

export function setupProfileView(): void {
  paintWho();
  byId("who-chip").addEventListener("click", () => openWho(true));
  byId("who-close").addEventListener("click", () => openWho(false));
  byId("who-scrim").addEventListener("click", () => openWho(false));
  byId("profile-switch").addEventListener("click", () => openWho(true));
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && document.body.classList.contains("who-open")) openWho(false);
  });
  byId("profile-new").addEventListener("submit", (event) => {
    event.preventDefault();
    const input = byId<HTMLInputElement>("profile-new-name");
    const name = input.value.trim();
    void run(async () => {
      const next = await call<ProfileBoard>(CHANNELS.altersCreate, name);
      const made = next.alters.find((alter) => alter.name === name);
      if (made) editing = made.id;
      input.value = "";
      return next;
    }, t("{name} added", { name }));
  });
  onViewShown("profile", () => void load());
}
